import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { API } from "@/data/site";

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ivory pt-24" data-testid="journal-post-not-found">
        <div className="text-center">
          <h1 className="font-serif text-5xl font-light text-espresso">This page has wandered off</h1>
          <Link to="/journal" className="mt-6 inline-block text-sm uppercase tracking-[0.2em] text-sienna" data-testid="not-found-back-link">
            Back to the journal
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return <main className="min-h-screen bg-ivory pt-24" data-testid="journal-post-loading" />;
  }

  return (
    <main className="min-h-screen bg-ivory pt-32 pb-24 lg:pt-40" data-testid="journal-post-page">
      <article className="mx-auto max-w-3xl px-6">
        <FadeUp>
          <Link
            to="/journal"
            data-testid="post-back-link"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-cocoa transition-colors hover:text-sienna"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> The journal
          </Link>
          <p className="mt-10 text-xs font-semibold uppercase tracking-[0.25em] text-sienna">
            {post.category} · {post.date} · {post.read_time}
          </p>
          <h1 className="mt-5 font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl" data-testid="post-title">
            {post.title}
          </h1>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="clip-arch mt-12 overflow-hidden border border-espresso/15">
            <img src={post.image} alt={post.title} className="aspect-[16/9] w-full object-cover" data-testid="post-image" />
          </div>
        </FadeUp>

        <FadeUp delay={0.25}>
          <div className="mt-12 space-y-7 text-lg leading-relaxed text-cocoa" data-testid="post-content">
            {post.content.map((para, i) => (
              <p key={i} className={i === 0 ? "font-serif text-2xl font-light leading-relaxed text-espresso" : ""}>
                {para}
              </p>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.3}>
          <div className="mt-16 rounded-2xl border border-espresso/15 bg-cream p-8 text-center">
            <p className="font-serif text-2xl font-light text-espresso">
              Walking into the fourth trimester soon?
            </p>
            <Link
              to="/book"
              data-testid="post-book-button"
              className="mt-6 inline-block rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark"
            >
              Book a visit
            </Link>
          </div>
        </FadeUp>
      </article>
    </main>
  );
}
